import { AfterViewInit, Directive, ElementRef, Input, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { DestroyBase } from '../../../components/common/ng-destroy-base';
import { EventBusService } from '../../../services/event-bus.service';
import { TextWidgetComponent } from '../widgets/OneReport/text-widget/text-widget.component';
import { WidgetBaseComponent } from '../widgets/widgetBase';
import { HashtagService } from './services/hashtag.service';

/**
 * Directive for setting real value for hashtags
 */
@Directive({
  selector: '[hashtagsetvalue]'
})
export class HashtagSetValueDirective extends DestroyBase implements OnInit, AfterViewInit {
  /**
   * Page where widget is displayed
   */
  @Input() page: any;

  /**
   * Widget where directive is applied
   */
  @Input() widget: WidgetBaseComponent;

  /**
   * Page where widget is displayed
   */
  private pageNum: number;

  /**
   * Is DOM element textarea
   */
  private isTextArea: boolean;

  /**
   * set value directives per widget
   */
  private static hashtagSetValueDirectivesPerWidget = new Map<string, number>();

  /**
   * Constructor
   * @param el
   * @param hashtagService
   * @param eventBus
   */
  constructor(private el: ElementRef,
    private hashtagService: HashtagService,
    private eventBus: EventBusService) {
    super();
  }

  /**
   * ngOnInit event
   */
  ngOnInit(): void {
    const map = HashtagSetValueDirective.hashtagSetValueDirectivesPerWidget;
    const widgetId = String(this.widget.widgetConfig?.widgetSettings.id);
    if (!map.has(widgetId)) {
      map.set(widgetId, 1);
    } else {
      map.set(widgetId, map.get(widgetId) + 1);
    }
  }

  /**
   * ngAfterViewInit event
   */
  ngAfterViewInit(): void {
    const page = this.getPage();

    this.setPageNo(page);

    const domEl = this.el.nativeElement;
    this.isTextArea = this.el.nativeElement.nodeName === 'TEXTAREA';

    const currentText = this.isTextArea ? domEl.value : domEl.innerText;
    const hashtags = currentText.match(/#=\w+=#/g) || [];
    //if page is not in edit mode on initial load, replace hashtags with real values
    if (!this.page?.editMode) {
      this.handleViewMode(currentText, domEl, hashtags);
    } else {
      this.replaceHashTagWithValue(hashtags);
    }
  }

  /**
   * Handles view mode actions
   * @param currentText
   * @param domEl
   * @param hashtags
   */
  private handleViewMode(currentText: any, domEl: any, hashtags: any) {
    this.onEvents.push(this.eventBus.subscribeEvent('loadHashtags', (widgetId) => {
      if (widgetId === this.widget.widgetConfig?.widgetSettings.id) {
        ({ currentText, hashtags } = this.tryRefreshCurrentText(currentText, domEl, hashtags));

        const hashtagsDetailsObservables = this.hashtagService.createHashtagDetailsObservables(hashtags, this.pageNum, this.widget.widgetConfig.widgetId);
        if (hashtagsDetailsObservables && hashtagsDetailsObservables.length > 0) {
          forkJoin(hashtagsDetailsObservables).subscribe(() => {
            this.replaceHashTagWithValue(hashtags);
            this.tryCallWidgetDataLoaded(this.widget?.widgetConfig?.widgetSettings.id);
          });
        } else {
          this.tryCallWidgetDataLoaded(this.widget.widgetConfig?.widgetSettings.id);
        }
      }
    }));
  }

  /**
   * Tries to refresh the current text
   * @param currentText
   * @param domEl
   * @param hashtags
   * @returns
   */
  private tryRefreshCurrentText(currentText: any, domEl: any, hashtags: any) {
    //TODO(SJ): currentText is sometimes not set in ngAfterViewInit yet, so try get it now once more
    if (!currentText) {
      currentText = this.isTextArea ? domEl.value : domEl.innerText;
      hashtags = currentText.match(/#=\w+=#/g) || [];
    }
    return { currentText, hashtags };
  }

  /**
   * Try to call the widget data loaded callbacks
   * @param widgetId
   */
  private tryCallWidgetDataLoaded(widgetId: string): void {
    //call widgetDataLoaded only when all hashtag directives on widget are processes
    const map = HashtagSetValueDirective.hashtagSetValueDirectivesPerWidget;
    //mark processed
    map.set(widgetId, map.get(widgetId) - 1);

    //only if last one call widgetDataLoaded
    if (map.get(widgetId) === 0) {
      this.page?.widgetDataLoaded(widgetId);
    }
  }

  /**
   * Gets the page
   * @returns
   */
  private getPage() {
    let page = this.page;
    if (!page) {
      page = this.widget.page;
    }
    return page;
  }

  /**
   * Sets the page number
   * @param page
   */
  private setPageNo(page: any) {
    if (page) {
      const index = page.document?.Data?.pages?.map(p => p.options)?.indexOf(page.options);
      if (index >= 0) {
        this.pageNum = 1 + index;
      }
      else {
        this.pageNum = 1;
      }
    }
  }

  /**
   * Replaces hashtags with values
   * @param hashtags
   */
  private replaceHashTagWithValue(hashtags: any[]) {
    // if there are already selected hashtags add them to hashtag list
    for (const h of hashtags) {
      const key = h.replace('#=', '').replace('=#', '');
      // get real value for hashtag
      this.setHashtagValue(key, h);
    }
  }

  /**
   * Sets a hashtag's value
   * @param key
   * @param hashtag
   */
  private setHashtagValue(key, hashtag) {
    this.hashtagService.getHashtagDtoDetails(key).subscribe(hashtagDetails => {
      if (hashtagDetails) {
        //text widget can (are) using HTML so we can't just set innerText
        const isTextWidget = this.widget instanceof TextWidgetComponent;
        if (!isTextWidget) {
          const domEl = this.el.nativeElement;
          const currentValue = this.isTextArea ? domEl.value : domEl.innerText;
          const newValue = currentValue.replace(hashtag, this.hashtagService.formatHashtagValue(hashtagDetails.value, hashtagDetails));

          //Textarea don't have innerText, we should use value 

          if (!this.isTextArea) {
            domEl.innerText = newValue;
          } else {
            domEl.value = newValue;
          }
        } else {
          const textWidget = this.widget as TextWidgetComponent;
          const currentValue = textWidget.previewContent as string;
          const newValue = currentValue.replace(hashtag, this.hashtagService.formatHashtagValue(hashtagDetails.value, hashtagDetails));
          textWidget.previewContent = TextWidgetComponent.removeUnwantedTag(newValue);
        }
      }
    });
  }
}
